import { Box, styled, Typography } from '@mui/material';
import { ArrowBackIosRounded } from '@mui/icons-material';
import clsx from 'clsx';
import { Link } from 'components/lib';
import { useRouter } from 'next/router';
import React, { ReactElement } from 'react';

export interface BackButtonProps {
  variant?: 'outlined' | 'filled';
  href?: string;
  onClick?: () => void;
  children?: string;
  className?: string;
  classes?: Partial<Record<'root' | 'arrow', string>>;
}

export const BackButton = styled(
  ({
    variant = 'outlined',
    href,
    onClick,
    children,
    className,
    classes: propClasses = {},
  }: BackButtonProps): ReactElement => {
    const hasChildren = !!children;

    const router = useRouter();

    if (!href && !onClick) onClick = () => router.back();

    return (
      <Link
        href={href}
        onClick={onClick}
        sx={{ '&:hover': { textDecoration: 'none' } }}
      >
        <Box
          className={clsx(className, propClasses.root)}
          display="flex"
          alignItems="center"
          lineHeight={0}
          pr={children ? 8 : 0}
          role="button"
          sx={{
            background: variant === 'outlined' ? '#fff' : '#e8eaef',
            border: theme =>
              variant === 'outlined'
                ? `1px solid ${theme.palette.neutral.dark}`
                : 'none',
            borderRadius: 16,
            height: hasChildren ? 32 : 30,
            minWidth: 30,
            color: theme =>
              variant === 'outlined'
                ? theme.palette.neutral.darkest
                : theme.palette.primary.main,
            cursor: 'pointer',
            outline: 'none',
            '&:focus-visible': {
              outline: theme => `2px auto ${theme.palette.primary.main}`,
              outlineOffset: 6,
            },
          }}
        >
          <Box
            ml={8}
            lineHeight={0}
            sx={{ '& .arrow': { fontSize: '0.8125em', lineHeight: 0 } }}
          >
            <ArrowBackIosRounded className={clsx('arrow', propClasses.arrow)} />
          </Box>
          {children && (
            <Typography
              sx={{
                marginLeft: 4,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 0,
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              {children}
            </Typography>
          )}
        </Box>
      </Link>
    );
  },
)``;
